import { Card, Col, Drawer, Row, Spin, Tag } from 'antd';
import { CreditCardOutlined, QrcodeOutlined } from '@ant-design/icons';
import '../../pages.scss';
import './QR.scss';
import '../../../../styles/Summaries.css';
import { useAuth } from '../../../../store/AuthContext/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import { authRequest } from '../../../../common/request/authRequest';
import moment from 'moment';
import { Column } from '@ant-design/charts';
import SelectPeriod from '../../../components/model/selectMonths/SelectPeriod';
import { PageFilters, PageFilterField } from '../../common/PageFilters';
import { DrawerPanelTitle, drawerPanelStyles } from '../../common/Drawers/DrawerPanelTitle';
import { EmptyStateBox } from '../../../components/design/EmptyStateBox/EmptyStateBox';

type TimeSeriesData = {
	x: string;
	value: number;
	category: string;
};

const CHART_COLORS = [
	'rgba(123, 168, 194, 0.92)',
	'rgba(45, 164, 133, 0.88)',
	'rgba(100, 116, 139, 0.85)',
	'rgba(124, 58, 237, 0.72)',
	'rgba(14, 116, 144, 0.85)',
	'rgba(71, 85, 105, 0.88)',
];

const ChartEmpty = ({ title }: { title: string }) => (
	<div style={{
		height: 230,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '0 16px',
	}}>
		<EmptyStateBox
			subtle
			title={title}
			style={{ width: '100%', maxWidth: 320, background: 'transparent', border: 'none' }}
		/>
	</div>
);

const calculateCategoryTotals = (data: TimeSeriesData[]): Map<string, number> => {
	const totals = new Map<string, number>();
	data?.forEach((item) => {
		totals.set(item.category, (totals.get(item.category) || 0) + Number(item.value || 0));
	});
	return totals;
};

export const HistoricQRScansDrawer = (props: { location: any, onFinish: any }) => {

	const { projectGroup } = useAuth();
	const [history, setHistory] = useState<TimeSeriesData[] | null>(null);
	const [selectedType, setSelectedType] = useState('LAST_3MONTHS');

	useEffect(() => {
		setHistory(null);
		authRequest(
			`${process.env.REACT_APP_API}/projects/${projectGroup.project}/monitoring/qr/${props.location.placeId}?filterPeriod=${selectedType}`,
			{ method: 'GET' }
		).then((data) => {
			setHistory(data);
		});
	}, [selectedType, projectGroup.project, props.location.placeId]);

	const categoryTotals = useMemo(
		() => calculateCategoryTotals(history || []),
		[history]
	);

	const totalScans = useMemo(
		() => Array.from(categoryTotals.values()).reduce((sum, value) => sum + value, 0),
		[categoryTotals]
	);

	const categoryRows = useMemo(
		() => Array.from(categoryTotals.entries()).sort((a, b) => b[1] - a[1]),
		[categoryTotals]
	);

	const hasData = (history?.length || 0) > 0 && totalScans > 0;

	const config: any = {
		isGroup: true,
		xField: 'x',
		yField: 'value',
		seriesField: 'category',
		color: CHART_COLORS,
		maxColumnWidth: 28,
		columnStyle: {
			radius: [4, 4, 0, 0],
			stroke: 'rgba(255, 255, 255, 0.5)',
			lineWidth: 1,
		},
		appendPadding: [8, 8, 0, 8],
		label: false,
		legend: {
			position: 'top-right',
			marker: {
				symbol: 'circle',
			},
			itemName: {
				style: {
					fill: '#64748b',
					fontSize: 12,
				},
			},
		},
		tooltip: {
			shared: true,
			showMarkers: false,
			title: (title: string) => {
				const parsed = moment(title, 'YYYY-MM-DD', true);
				return parsed.isValid() ? parsed.format('DD MMM YYYY') : title;
			},
		},
		xAxis: {
			line: null,
			tickLine: null,
			label: {
				formatter: (text: any) => {
					const parsed = moment(text, 'YYYY-MM-DD', true);
					return parsed.isValid() ? parsed.format('DD MMM') : text;
				},
				autoHide: true,
				autoRotate: false,
				style: {
					fill: '#94a3b8',
					fontSize: 11,
				},
			},
		},
		yAxis: {
			grid: {
				line: {
					style: {
						stroke: '#f1f5f9',
						lineWidth: 1,
					},
				},
			},
			label: {
				style: {
					fill: '#94a3b8',
					fontSize: 11,
				},
			},
		},
		meta: {
			value: {
				alias: 'Escaneos',
			},
		},
	};

	return (
		<Drawer
			placement="right"
			width={'85%'}
			open={true}
			onClose={() => props.onFinish()}
			{...drawerPanelStyles}
			title={
				props.location && (
					<DrawerPanelTitle
						title="Monitoreo de reseñas solicitadas"
						location={props.location}
					/>
				)
			}
		>
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
				<PageFilters>
					<PageFilterField label='Periodo'>
						<SelectPeriod
							setSelectedType={setSelectedType}
							defaultValue={selectedType}
							maxMonths={12}
						/>
					</PageFilterField>
				</PageFilters>
			</div>

			{history ? (
				<>
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={8}>
							<Card
								style={{ border: 'none' }}
								bodyStyle={{
									backgroundColor: 'white',
									borderRadius: 16,
									height: 88,
									padding: '10px 14px',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
								}}
							>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
									<span style={{ fontSize: 11, color: '#9F9F9F', fontWeight: 500 }}>Total escaneos</span>
									<Tag style={{
										borderRadius: 10,
										margin: 0,
										fontSize: 11,
										lineHeight: '18px',
										padding: '0 8px',
										color: '#64748b',
										background: '#f1f5f9',
										border: '1px solid #e2e8f0',
									}}>
										Periodo
									</Tag>
								</div>
								<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
									<QrcodeOutlined style={{ fontSize: 22, color: '#7b93a8' }} />
									<span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#475569' }}>
										{totalScans.toLocaleString()}
									</span>
								</div>
							</Card>
						</Col>
						<Col span={8}>
							<Card
								style={{ border: 'none' }}
								bodyStyle={{
									backgroundColor: 'white',
									borderRadius: 16,
									height: 88,
									padding: '10px 14px',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
								}}
							>
								<span style={{ fontSize: 11, color: '#9F9F9F', fontWeight: 500 }}>Tarjetas con escaneos</span>
								<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
									<CreditCardOutlined style={{ fontSize: 22, color: '#7b93a8' }} />
									<span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#475569' }}>
										{categoryRows.length}
									</span>
								</div>
							</Card>
						</Col>
						<Col span={8}>
							<Card
								style={{ border: 'none' }}
								bodyStyle={{
									backgroundColor: 'white',
									borderRadius: 16,
									height: 88,
									padding: '10px 14px',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
								}}
							>
								<span style={{ fontSize: 11, color: '#9F9F9F', fontWeight: 500 }}>Media por tarjeta</span>
								<span style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#475569', marginTop: 4 }}>
									{categoryRows.length
										? Math.round(totalScans / categoryRows.length).toLocaleString()
										: 0}
								</span>
							</Card>
						</Col>
					</Row>

					<Row gutter={36}>
						<Col span={16}>
							<Card
								bodyStyle={{ backgroundColor: 'white', borderRadius: 25, height: 327 }}
								style={{ border: 'none' }}
							>
								<div className="title-charts">
									<h1>Escaneos en el periodo</h1>
								</div>
								<div style={{ padding: '12px 20px 8px 16px' }}>
									{hasData ? (
										<Column data={history} height={230} {...config} />
									) : (
										<ChartEmpty title="Aún no hay escaneos en el periodo seleccionado" />
									)}
								</div>
							</Card>
						</Col>
						<Col span={8}>
							<Card
								style={{ border: 'none', height: '100%' }}
								bodyStyle={{
									backgroundColor: 'white',
									borderRadius: 25,
									height: 327,
									padding: '22px 24px',
									display: 'flex',
									flexDirection: 'column',
								}}
							>
								<h1 style={{ margin: 0, color: '#9F9F9F', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
									Totales por tarjeta
								</h1>
								{!categoryRows.length ? (
									<div style={{
										flex: 1,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}>
										<EmptyStateBox
											subtle
											title="Sin escaneos"
											style={{ width: '100%', background: 'transparent', border: 'none' }}
										/>
									</div>
								) : (
									<div style={{
										flex: 1,
										overflowY: 'auto',
										display: 'flex',
										flexDirection: 'column',
										gap: 8,
									}}>
										{categoryRows.map(([category, total], index) => (
											<div
												key={category}
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between',
													gap: 12,
													padding: '10px 12px',
													background: '#f8fafc',
													borderRadius: 12,
												}}
											>
												<div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
													<span style={{
														width: 10,
														height: 10,
														borderRadius: '50%',
														background: CHART_COLORS[index % CHART_COLORS.length],
														flexShrink: 0,
													}} />
													<span style={{
														fontSize: 13,
														fontWeight: 600,
														color: '#334155',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														whiteSpace: 'nowrap',
													}}>
														{category}
													</span>
												</div>
												<span style={{
													fontSize: 14,
													fontWeight: 700,
													color: '#475569',
													fontVariantNumeric: 'tabular-nums',
													flexShrink: 0,
												}}>
													{total.toLocaleString()}
												</span>
											</div>
										))}
									</div>
								)}
							</Card>
						</Col>
					</Row>
				</>
			) : (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
					<Spin />
				</div>
			)}
		</Drawer>
	);
};
